import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, History, Plus, Send, Sparkles, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge, Button, Spinner, Textarea } from '@/components/ui';
import { errorMessage, http, postStream } from '@/lib/api';
import { useUi } from '@/lib/ui-store';
import { cn } from '@/lib/utils';

interface Proposal {
  id: string;
  toolName: string;
  args: Record<string, unknown>;
  preview: { summary: string; fields: Record<string, unknown>; steps?: { n: number; tool: string; summary: string; why: string | null }[]; source?: string };
  status: 'proposed' | 'confirmed' | 'cancelled' | 'failed';
  resultEntity?: Record<string, unknown> | null;
  error?: string | null;
}
interface Msg {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: string[];
  aiRequestId?: string | null;
  tools?: string[];
  proposals?: Proposal[];
}

/** Linkifies item keys in assistant text (source chips, §19.11). */
function Linkified({ text }: { text: string }) {
  const parts = text.split(/(\b[A-Z][A-Z0-9]{1,9}-\d+\b)/g);
  return (
    <>
      {parts.map((p, i) =>
        /^[A-Z][A-Z0-9]{1,9}-\d+$/.test(p) ? (
          <Link key={i} to={`/items/${p}`} className="item-chip">
            {p}
          </Link>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

/** Kanbix Assistant panel (FR-AI-30..37): context-aware, streaming, proposals need Confirm. */
export function AssistantPanel() {
  const { toggleAssistant, pageContext, assistantPrompt, clearPrompt } = useUi();
  const qc = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const abort = useRef<AbortController | null>(null);
  const { data: history } = useQuery({ queryKey: ['ai-conversations'], queryFn: () => http.get<{ id: string; title: string; pinned: boolean; updatedAt: string }[]>('/ai/conversations'), enabled: showHistory });

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = async (prompt?: string) => {
    const msg = (prompt ?? text).trim();
    if (!msg || busy) return;
    setText('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '', tools: [], proposals: [] }]);
    const patchLast = (fn: (m: Msg) => Msg) => setMessages((all) => [...all.slice(0, -1), fn(all[all.length - 1])]);
    abort.current = new AbortController();
    try {
      await postStream(
        `/ai/conversations/${conversationId ?? 'new'}/messages`,
        { text: msg, pageContext },
        (type, data) => {
          if (type === 'conversation') setConversationId(String(data.id));
          if (type === 'delta') patchLast((m) => ({ ...m, content: m.content + String(data.text) }));
          if (type === 'tool') patchLast((m) => ({ ...m, tools: [...(m.tools ?? []), String(data.name)] }));
          if (type === 'proposal') patchLast((m) => ({ ...m, proposals: [...(m.proposals ?? []), data.proposal as Proposal] }));
          if (type === 'done') {
            const d = data.message as { id: string; content: string; sources: string[]; aiRequestId: string | null };
            patchLast((m) => ({ ...m, id: d.id, content: d.content, sources: d.sources, aiRequestId: d.aiRequestId }));
          }
          if (type === 'error') patchLast((m) => ({ ...m, content: `⚠️ ${String(data.message)}` }));
        },
        abort.current.signal,
      );
    } catch (err) {
      patchLast((m) => ({ ...m, content: `⚠️ ${errorMessage(err)}` }));
    } finally {
      setBusy(false);
      void qc.invalidateQueries({ queryKey: ['ai-conversations'] });
    }
  };

  useEffect(() => {
    if (assistantPrompt) {
      void send(assistantPrompt);
      clearPrompt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantPrompt]);

  const openConversation = async (id: string) => {
    const c = await http.get<{ messages: { id: string; role: 'user' | 'assistant'; content: string; sources: string[]; aiRequestId: string | null }[]; proposals: Proposal[] }>(`/ai/conversations/${id}`);
    setConversationId(id);
    setMessages(c.messages.map((m) => ({ ...m, proposals: [] })));
    setShowHistory(false);
  };

  const decide = useMutation({
    mutationFn: ({ p, action, args }: { p: Proposal; action: 'confirm' | 'cancel'; args?: Record<string, unknown> }) => http.post<Proposal>(`/ai/proposals/${p.id}/${action}`, action === 'confirm' ? (args ? { args } : {}) : undefined),
    onSuccess: (updated) => {
      setMessages((all) => all.map((m) => ({ ...m, proposals: m.proposals?.map((x) => (x.id === updated.id ? updated : x)) })));
      if (updated.status === 'confirmed') {
        toast.success('Done', { description: updated.resultEntity?.key ? `Created/updated ${String(updated.resultEntity.key)}` : undefined });
        void qc.invalidateQueries();
      }
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const feedback = (aiRequestId: string, rating: 1 | -1) =>
    void http.post('/ai/feedback', { aiRequestId, rating }).then(() => toast.success('Thanks for the feedback'));

  const ctxLabel = pageContext.itemKey ?? (pageContext.documentId ? 'this document' : pageContext.releaseId ? 'this release' : pageContext.noteId ? 'this note' : pageContext.projectKey);

  return (
    <aside className="flex w-[380px] shrink-0 flex-col border-l border-border bg-surface">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
        <Sparkles className="h-4 w-4 text-brand" />
        <span className="font-semibold">Kanbix Assistant</span>
        <div className="ml-auto flex gap-0.5">
          <Button variant="ghost" size="icon" onClick={() => setShowHistory(!showHistory)} aria-label="History">
            <History className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setConversationId(null);
              setMessages([]);
            }}
            aria-label="New chat"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => toggleAssistant(false)} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {showHistory ? (
        <div className="flex-1 overflow-y-auto p-2">
          {(history ?? []).map((c) => (
            <button key={c.id} onClick={() => void openConversation(c.id)} className="block w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-muted">
              {c.title}
            </button>
          ))}
          {!history?.length && <div className="p-3 text-sm text-subtle">No conversations yet.</div>}
        </div>
      ) : (
        <div ref={scroller} className="scrollbar-thin flex-1 space-y-3 overflow-y-auto p-3">
          <AgentProposals />
          {!messages.length && (
            <div className="space-y-2 text-sm text-subtle">
              <p>Ask about your work, releases, documents and time. Changes are proposed as cards you confirm.</p>
              {['What should I work on next?', "What's blocking the current release?", 'What did I work on last week?', 'Create a task to update the install guide'].map((s) => (
                <button key={s} onClick={() => void send(s)} className="block w-full rounded-md border border-border px-2.5 py-1.5 text-left hover:bg-muted">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={cn('text-sm', m.role === 'user' && 'ml-8 rounded-lg bg-brand-soft px-3 py-2')}>
              {m.role === 'assistant' && !!m.tools?.length && (
                <div className="mb-1 flex flex-wrap gap-1">
                  {[...new Set(m.tools)].map((t) => (
                    <Badge key={t}>🔎 {t.replace(/_/g, ' ')}</Badge>
                  ))}
                </div>
              )}
              <div className="whitespace-pre-wrap">{m.content ? <Linkified text={m.content} /> : busy && i === messages.length - 1 ? <Spinner /> : null}</div>
              {m.proposals?.map((p) => (
                <ProposalCard key={p.id} p={p} busy={decide.isPending} onDecide={(action, args) => decide.mutate({ p, action, args })} />
              ))}
              {m.role === 'assistant' && m.aiRequestId && (
                <div className="mt-1 flex items-center gap-1 text-subtle">
                  <span className="text-[10px]">✦ AI-generated</span>
                  <button onClick={() => feedback(m.aiRequestId!, 1)} className="rounded p-0.5 hover:bg-muted" aria-label="Helpful">
                    <ThumbsUp className="h-3 w-3" />
                  </button>
                  <button onClick={() => feedback(m.aiRequestId!, -1)} className="rounded p-0.5 hover:bg-muted" aria-label="Not helpful">
                    <ThumbsDown className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="border-t border-border p-2">
        {ctxLabel && (
          <div className="mb-1.5">
            <Badge className="bg-brand-soft text-brand">Context: {ctxLabel}</Badge>
          </div>
        )}
        <div className="flex gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder="Ask Kanbix…"
            rows={2}
            className="min-h-[44px] resize-none"
          />
          <Button size="icon" onClick={() => void send()} disabled={busy || !text.trim()} aria-label="Send" className="self-end">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </aside>
  );
}

/** Changes proposed by external AI agents over MCP (FR-AI-38). Nothing happens until you confirm here. */
function AgentProposals() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['ai-proposals-pending'], queryFn: () => http.get<Proposal[]>('/ai/proposals/pending'), refetchInterval: 30_000 });
  const decide = useMutation({
    mutationFn: ({ id, action, args }: { id: string; action: 'confirm' | 'cancel'; args?: Record<string, unknown> }) => http.post<Proposal>(`/ai/proposals/${id}/${action}`, action === 'confirm' ? (args ? { args } : {}) : undefined),
    onSuccess: (p) => {
      if (p.status === 'confirmed') toast.success('Done', { description: p.resultEntity?.key ? `Created/updated ${String(p.resultEntity.key)}` : undefined });
      void qc.invalidateQueries();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const agent = (data ?? []).filter((p) => (p.preview as { source?: string }).source === 'mcp');
  if (!agent.length) return null;
  return (
    <div className="space-y-2 rounded-md border border-warning/40 bg-warning/5 p-2.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-warning">Pending approvals from AI agents ({agent.length})</div>
      {agent.map((p) => (
        <ProposalCard key={p.id} p={p} busy={decide.isPending && decide.variables?.id === p.id} onDecide={(action, args) => decide.mutate({ id: p.id, action, args })} />
      ))}
    </div>
  );
}

/** A proposed change. Plans (FR-AI-34) list their steps; untick steps to leave them out. */
function ProposalCard({ p, busy, onDecide }: { p: Proposal; busy: boolean; onDecide: (action: 'confirm' | 'cancel', args?: Record<string, unknown>) => void }) {
  const [skip, setSkip] = useState<number[]>([]);
  const steps = p.preview.steps;
  return (
    <div className="mt-2 rounded-md border border-brand/40 bg-brand-soft/40 p-2.5 text-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-brand">{steps ? 'Proposed plan' : 'Proposed action'}{p.preview.source === 'mcp' ? ' · from an AI agent' : ''}</div>
      <div className="mt-0.5 font-medium">{p.preview.summary}</div>
      {steps ? (
        <ol className="mt-1.5 space-y-1">
          {steps.map((st) => (
            <li key={st.n} className={cn('flex items-start gap-2 text-xs', skip.includes(st.n) && 'text-subtle line-through')}>
              {p.status === 'proposed' && (
                <input type="checkbox" className="mt-0.5" checked={!skip.includes(st.n)} onChange={(e) => setSkip((x) => (e.target.checked ? x.filter((n) => n !== st.n) : [...x, st.n]))} aria-label={`Include step ${st.n}`} />
              )}
              <span>
                <span className="font-medium">{st.n}.</span> {st.summary}
                {st.why && <span className="block text-subtle">{st.why}</span>}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <div className="mt-1 space-y-0.5 text-xs text-subtle">
          {Object.entries(p.preview.fields)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => (
              <div key={k}>
                <span className="font-medium">{k}:</span> {String(v)}
              </div>
            ))}
        </div>
      )}
      {p.status === 'proposed' ? (
        <div className="mt-2 flex gap-2">
          <Button size="sm" onClick={() => onDecide('confirm', steps && skip.length ? { skip } : undefined)} loading={busy}>
            <Check className="h-3.5 w-3.5" /> {steps ? `Run ${steps.length - skip.length} step(s)` : 'Confirm'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDecide('cancel')}>
            Cancel
          </Button>
        </div>
      ) : (
        <Badge className={cn('mt-2', p.status === 'confirmed' ? 'bg-success/15 text-success' : p.status === 'failed' ? 'bg-danger/10 text-danger' : '')}>
          {p.status}
          {Array.isArray(p.resultEntity?.keys) ? ` · ${(p.resultEntity!.keys as string[]).join(', ')}` : p.resultEntity?.key ? ` · ${String(p.resultEntity.key)}` : ''}
          {p.error ? ` · ${p.error}` : ''}
        </Badge>
      )}
    </div>
  );
}
